import { Award, Shield } from 'lucide-react';
import {
  ConductCaseCategory,
  ConductCaseResolutionType,
  ConductCaseSeverity,
} from '@sandtech-hub-monorepo/ehub-client';
import { Badge } from '@sandtech-hub-monorepo/ui-kit';
import {
  TranscriptTerm,
  getSeverityLabel,
  getCategoryLabel,
  getStatusLabel,
} from '../utils';

interface Props {
  term: TranscriptTerm;
}

const getStandingBadgeClass = (label: string): string => {
  if (label === 'Good Academic Standing') return 'text-foreground border-muted';
  if (label === 'Scholastic Warning')
    return 'bg-warning/10 text-warning border-warning/30';
  return 'bg-destructive/10 text-destructive border-destructive/30';
};

const getCaseSeverityBadgeClass = (
  severity: ConductCaseSeverity,
  resolutionType?: ConductCaseResolutionType | null
): string => {
  if (resolutionType === ConductCaseResolutionType.Remediated)
    return 'text-muted-foreground border-muted line-through';
  if (severity === ConductCaseSeverity.ScholasticWarning)
    return 'bg-warning/10 text-warning border-warning/30';
  return 'bg-destructive/10 text-destructive border-destructive/30';
};

const getCaseCategoryBadgeClass = (category: ConductCaseCategory): string => {
  if (category === ConductCaseCategory.AcademicIntegrityViolation)
    return 'bg-warning/10 text-warning border-warning/30';
  return 'bg-destructive/10 text-destructive border-destructive/30';
};

const TermStandingConduct = ({ term }: Props) => (
  <div className="m-3 rounded-lg border border-muted p-3 flex flex-col gap-4 sm:flex-row print:flex-row">
    <div className="flex items-start gap-3 sm:w-1/3 shrink-0">
      <div className="p-2 rounded-full bg-primary/10 text-primary shrink-0">
        <Award className="w-5 h-5" />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">
          Academic Standing
        </span>
        <Badge
          variant="outline"
          className={`w-fit ${getStandingBadgeClass(
            term.academicStandingLabel
          )}`}
        >
          {term.academicStandingLabel}
        </Badge>
      </div>
    </div>

    <div className="hidden w-px self-stretch bg-muted sm:block print:block" />

    <div className="flex items-start gap-3 flex-1 min-w-0">
      <div className="p-2 rounded-full bg-primary/10 text-primary shrink-0">
        <Shield className="w-5 h-5" />
      </div>
      <div className="flex flex-col gap-1 w-full min-w-0">
        <span className="text-xs font-medium text-muted-foreground">
          Conduct Cases
        </span>
        {term.conductCases.length === 0 ? (
          <span className="text-sm text-muted-foreground">None</span>
        ) : (
          <div className="space-y-1.5">
            {term.conductCases.map((c, i) => (
              <div
                key={i}
                className="text-sm flex flex-wrap items-center gap-1.5"
              >
                <span className="text-muted-foreground font-medium">
                  {i + 1}.
                </span>
                <Badge
                  variant="outline"
                  className={getCaseSeverityBadgeClass(
                    c.severity,
                    c.resolutionType
                  )}
                >
                  {getSeverityLabel(c.severity)}
                </Badge>
                <Badge
                  variant="outline"
                  className={getCaseCategoryBadgeClass(c.category)}
                >
                  {getCategoryLabel(c.category)}
                </Badge>
                <span className="font-medium text-foreground">Class:</span>
                <span className="text-muted-foreground">
                  {c.className ?? 'N/A'}
                </span>
                <Badge variant="outline" className="text-muted-foreground">
                  {getStatusLabel(c.status, c.resolutionType)}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

export default TermStandingConduct;
